import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PdfPreviewModal from '../components/PdfPreviewModal';

/**
 * Context globale per visualizzare PDF in-app.
 *
 * Motivazione (bug PWA iOS): aprire un PDF via `window.open(blob:..., '_blank')`
 * o tramite `<a download>` su iOS PWA in modalità standalone dirotta il webview,
 * e quando l'utente chiude il PDF si chiude anche la PWA.
 *
 * Soluzione: rendere il PDF sempre in un iframe DENTRO l'app (PdfPreviewModal).
 * Chiudere il modal non tocca il webview della PWA.
 *
 * Uso:
 *   const { showPdfFromResponse, showPdfFromBlob } = usePdfViewer();
 *   const res = await api.get('/export-pdf', { responseType: 'blob' });
 *   showPdfFromResponse(res, 'documento.pdf');
 */
const PdfViewerContext = createContext(null);

export const usePdfViewer = () => {
  const ctx = useContext(PdfViewerContext);
  if (!ctx) {
    // Fallback sicuro: se il provider non è montato, apre in nuova tab
    // (non dovrebbe mai capitare se App è wrappato correttamente)
    return {
      showPdfFromBlob: (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'documento.pdf';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      showPdfFromResponse: (response, filename) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'documento.pdf';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
    };
  }
  return ctx;
};

export const PdfViewerProvider = ({ children }) => {
  const [state, setState] = useState({ url: null, filename: null });

  const showPdfFromBlob = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob);
    setState({ url, filename: filename || 'documento.pdf' });
  }, []);

  const showPdfFromResponse = useCallback((response, filename) => {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    setState({ url, filename: filename || 'documento.pdf' });
  }, []);

  const hidePdf = useCallback(() => {
    setState({ url: null, filename: null });
  }, []);

  // Cleanup URL su unmount definitivo (safety)
  useEffect(() => {
    return () => {
      if (state.url && state.url.startsWith('blob:')) {
        try { URL.revokeObjectURL(state.url); } catch { /* noop */ }
      }
    };
  }, [state.url]);

  return (
    <PdfViewerContext.Provider value={{ showPdfFromBlob, showPdfFromResponse, hidePdf }}>
      {children}
      {state.url && (
        <PdfPreviewModal
          pdfUrl={state.url}
          fileName={state.filename}
          onClose={hidePdf}
        />
      )}
    </PdfViewerContext.Provider>
  );
};

export default PdfViewerContext;
